import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "../app/navigation.js";
import type { Seat } from "@guandan/engine";
import { api, ApiError, type LobbyInfo } from "../net/api.js";
import { layout, replayLayout } from "../game/layout.js";
import { SeatRing } from "../game/SeatRing.js";
import { useSize } from "../game/useSize.js";
import { refreshSession, useSession } from "../net/session.js";
import { usePlayerState } from "../app/ChannelProvider.js";
import { useActivity } from "../app/ActivityController.js";
import { BottomSheet, Button, Coins, ErrorNote, PageHeader, Panel, Spinner } from "../ui/primitives.js";
import { MatchConfigurationSummary } from "../ui/GameFlow.js";
import { useLocale } from "../i18n.js";
import "./table.css";
import "./room-wait.css";

/**
 * The room, before the deal.
 *
 * One screen for both ways in. A host-started lobby shows a code to read out
 * and waits for the host; a matchmade lobby has its four seats already chosen
 * and waits for people to confirm they are still there.
 *
 * It seats players with the very component the table uses, on the very same
 * computed geometry, because this is the screen a player looks at in the second
 * before the cards appear — a seating arrangement that jumps at that moment
 * reads as the game restarting rather than beginning.
 */
export function RoomWait({ wide = false }: { wide?: boolean }) {
  const { t, number, locale } = useLocale();
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const { refresh: refreshActivity } = useActivity();
  const myId = session.status === "signedIn" ? session.me.user.id : null;

  const [info, setInfo] = useState<LobbyInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const seenReadyCheck = useRef(false);
  const surface = useSize();

  // The countdown is the only thing ticking between polls, so it gets its own
  // cheap timer rather than a faster poll that would wake the lobby needlessly.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const enterRoom = useCallback(async (roomId: string) => {
    await refreshSession();
    navigate(`/table/${roomId}`, { replace: true });
  }, [navigate]);

  const poll = useCallback(async () => {
    try {
      const next = await api.lobbyInfo(code);
      if (next.kind === "ready") seenReadyCheck.current = true;
      if (next.state === "matched") {
        // The room id on the lobby is authoritative; `/api/me` may not have
        // caught up with the hub write yet on the very first poll after start.
        if (next.roomId !== null) await enterRoom(next.roomId);
        else {
          const me = await api.me();
          if (me.currentRoom !== null) await enterRoom(me.currentRoom);
        }
        return;
      }
      setInfo(next);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // The channel event that opened this screen is only a delivery hint.
        // Reconcile before showing a terminal state so an expired ready room
        // cannot remain the player's current activity and bounce Home straight
        // back here. If the room was dealt while this request raced its cleanup,
        // the authoritative activity also gives us the table to enter.
        const activity = await refreshActivity();
        if (activity?.type === "match") {
          await enterRoom(activity.roomId);
          return;
        }
        // A ready lobby that has gone is one nobody confirmed in time. That is
        // a different thing from a room that vanished, and the player deserves
        // to be told nothing was taken from them.
        setError(seenReadyCheck.current ? t("declined") : t("roomGone"));
      }
    }
  }, [code, enterRoom, refreshActivity, t]);

  // The matchmaker publishes every seat, readiness and countdown change to the
  // players in the lobby, so this re-reads when told to rather than twice a
  // second on the chance that something moved. The slow interval underneath is
  // a safety net for a dropped socket, not the mechanism.
  const lobbyTick = usePlayerState((state) => state.lobbyTick);
  useEffect(() => {
    void poll();
    const id = window.setInterval(() => void poll(), 15_000);
    return () => window.clearInterval(id);
  }, [poll, lobbyTick]);

  const mySeat = useMemo(
    () => info?.members.find((member) => member.userId === myId)?.seat ?? null,
    [info, myId],
  );
  const table = useMemo(
    () => wide
      ? replayLayout(Math.max(1, surface.width), Math.max(1, surface.height), mySeat as Seat | null, true)
      : layout(Math.max(1, surface.width), Math.max(1, surface.height), mySeat as Seat | null),
    [surface.width, surface.height, mySeat, wide],
  );

  async function ready(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await api.setReady(code, next);
      setInfo(result);
      // Confirming last starts the match from here. The lobby carries a room id
      // from the moment it is created, so only the state says it is real.
      if (result.state === "matched" && result.roomId !== null) await enterRoom(result.roomId);
    } catch (caught) {
      setError(caught instanceof ApiError && caught.code === "lobby_abandoned" ? t("lobbyGone") : t("startMatchError"));
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.startLobby(code);
      await enterRoom(result.roomId);
    } catch {
      setError(t("startMatchError"));
      setBusy(false);
    }
  }

  function leave() { if (info?.state !== "starting") setLeaveOpen(true); }
  async function confirmLeave() {
    if (busy || info?.state === "starting") return;
    setBusy(true); setError(null);
    try {
      if (myId !== null) await api.leaveLobby(code, myId);
      await refreshActivity();
      navigate("/");
    } catch { setError(t("somethingWrong")); setLeaveOpen(false); }
    finally { setBusy(false); }
  }

  async function share() {
    const text = `Join my Guandan table — code ${code}`;
    try {
      if (typeof navigator.share === "function") await navigator.share({ text });
      else {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      }
    } catch {
      // The player dismissed the share sheet; the code is on screen regardless.
    }
  }

  if (error !== null && info === null) {
    return (
      <Panel>
        <ErrorNote>{error}</ErrorNote>
        <Button onClick={() => navigate("/")}>{t("backLobby")}</Button>
      </Panel>
    );
  }
  if (info === null) return <Spinner label={t("loading")} />;

  const isHost = info.hostUserId === myId;
  const isReadyCheck = info.kind === "ready";
  const members = info.members;
  const readyCount = members.filter((member) => member.ready === true).length;
  const iAmReady = members.find((member) => member.userId === myId)?.ready === true;
  const countdownStarted = info.state === "starting";
  const secondsLeft = info.readyDeadline === null
    ? null
    : Math.max(0, Math.ceil((info.readyDeadline - now) / 1000));
  const readyCheckCountingDown = isReadyCheck && secondsLeft !== null;
  const bySeat = new Map(info.members.flatMap((member) => (member.seat === null ? [] : [[member.seat, member] as const])));
  const unseated = info.members.filter((member) => member.seat === null);

  const tableStage = (
    <>
      {!wide && (
        <div className="wait-meta-grid">
          <div>
            <span>{t("entryFee")}</span>
            <strong>{info.entryCoins > 0 ? <Coins amount={info.entryCoins} /> : t("freeEntry")}</strong>
          </div>
          <div>
            <span>{t("ruleset")}</span>
            <strong>{rulesetName(info.rulesetId, t)}</strong>
          </div>
        </div>
      )}
      {/* The seat ring uses the live table's geometry, so nobody jumps to a
          different side when the cards are dealt. */}
      <div className="wait-table" ref={surface.ref}>
          {surface.width > 0 && (
            <SeatRing
              table={table}
              entryFor={(seat) => {
                const member = bySeat.get(seat) ?? null;
                if (member === null) {
                  return { name: t("emptySeat"), avatarId: "avatar-01", empty: true, badge: "—" };
                }
                return {
                  name: member.name,
                  avatarId: member.avatarId,
                  // In a ready-check the ring marks who the table is still
                  // waiting on, which is the same "we need you" signal it
                  // carries during play.
                  onTurn: isReadyCheck && member.ready !== true,
                  badge: number(member.rating),
                  note: readyNote(member, { isReadyCheck, isHost: member.userId === info.hostUserId, t }),
                };
              }}
            />
          )}
          <div className={`wait-centre${countdownStarted ? " wait-centre-countdown" : ""}`}>
            {isReadyCheck ? (
              <>
                <span>{countdownStarted ? t("allPlayersReady") : t("readyWaiting")}</span>
                {readyCheckCountingDown
                  ? <strong className="tabular">{secondsLeft}</strong>
                  : <strong className="tabular">{readyCount}/{members.length}</strong>}
                <small>
                  {countdownStarted
                    ? t("readyIn", { count: secondsLeft ?? 0 })
                    : readyCheckCountingDown
                      ? t("readyDeadlineIn", { count: secondsLeft ?? 0, ready: readyCount, total: members.length })
                      : t("readyHint")}
                </small>
              </>
            ) : (
              <>
                <span>{t("roomCode")}</span>
                <strong className="wait-centre-code tabular">{info.code}</strong>
                <small>{t("teamHint")}</small>
              </>
            )}
          </div>
      </div>
    </>
  );

  const roomActions = isReadyCheck ? (
    <>
          <Button
            variant={iAmReady ? "secondary" : "primary"}
            size="lg"
            busy={busy}
            disabled={countdownStarted}
            onClick={() => void ready(!iAmReady)}
          >
            {countdownStarted ? t("allPlayersReady") : iAmReady ? t("cancelReady") : t("readyUp")}
          </Button>
          {!countdownStarted && <Button variant="ghost" onClick={leave}>{t("leave")}</Button>}
    </>
  ) : (
    <>
          <div className="room-code-panel">
            <Button variant="secondary" onClick={share}>
              {copied ? t("copied") : t("shareCode")}
            </Button>
          </div>
          {isHost && <Button variant="secondary" onClick={() => void api.setLobbyVisibility(code, info.visibility === "private" ? "public" : "private").then(setInfo).catch(() => setError(t("somethingWrong")))}>{info.visibility === "private" ? t("enablePublicSpectate") : t("disablePublicSpectate")}</Button>}
          {unseated.length > 0 && (
            <p className="section-hint">{t("waiting")} {unseated.map((m) => m.name).join(", ")}</p>
          )}
          <div className="wait-row-actions">
            {isHost
              ? <Button variant="primary" size="lg" busy={busy} onClick={start}>{t("startMatch")}</Button>
              : <p className="section-hint">{t("waitingHost")}</p>}
            <Button variant="ghost" onClick={leave}>{t("leave")}</Button>
          </div>
    </>
  );

  return (
    <div className={`wait-screen${wide ? " wait-screen-wide" : ""}`}>
      {error && <ErrorNote>{error}</ErrorNote>}
      {wide ? (
        <PageHeader
          eyebrow={isReadyCheck ? t("matchReadyTitle") : t("waitingRoom")}
          title={info.mode === "ranked" ? t("ranked") : t("goldMode")}
          subtitle={isReadyCheck ? t("readyHint") : t("teamHint")}
          onBack={() => void leave()}
          backDisabled={countdownStarted}
          action={<span className={`wait-status-chip${countdownStarted ? " is-live" : ""}`}>{countdownStarted ? t("allPlayersReady") : isReadyCheck ? t("readyCount", { ready: readyCount, total: members.length }) : t("waiting")}</span>}
        />
      ) : (
        <header className="wait-toolbar">
          <button type="button" className="wait-back" onClick={() => void leave()} disabled={countdownStarted} aria-label={t("backLobby")}><ArrowLeft size={20} aria-hidden="true" /></button>
          <div>
            <p>{isReadyCheck ? t("matchReadyTitle") : t("waitingRoom")}</p>
            <h1>{info.mode === "ranked" ? t("ranked") : t("goldMode")}</h1>
          </div>
        </header>
      )}

      {wide ? (
        <Panel tone="felt" className="wait-room-card" aria-label={t("roomInfo")}>{tableStage}</Panel>
      ) : (
        <section className="wait-room-card" aria-label={t("roomInfo")}>{tableStage}</section>
      )}

      {wide ? (
        <MatchConfigurationSummary
          eyebrow={t("roomInfo")}
          title={isReadyCheck ? t("matchReadyTitle") : info.code}
          rows={[
            { label: t("ruleset"), value: rulesetName(info.rulesetId, t) },
            { label: t("entryFee"), value: info.entryCoins > 0 ? <Coins amount={info.entryCoins} /> : t("freeEntry") },
            { label: t("roomCode"), value: info.code },
            { label: t("roomVisibility"), value: info.visibility === "public" ? t("roomPublic") : t("roomPrivate") },
          ]}
          status={isReadyCheck ? t("readyCount", { ready: readyCount, total: members.length }) : unseated.length > 0 ? `${t("waiting")} ${unseated.map((member) => member.name).join(", ")}` : t("waitingHost")}
          actions={roomActions}
          className="wait-summary"
        />
      ) : (
        <section className="wait-actions">{roomActions}</section>
      )}
      <BottomSheet open={leaveOpen} title={t("leave")} onClose={() => { if (!busy) setLeaveOpen(false); }} footer={<Button variant="danger" busy={busy} onClick={() => void confirmLeave()}>{t("leave")}</Button>}>
        <p>{locale === "zh-CN" ? "离开后将清除你的准备状态。确定离开这个房间？" : "Leaving clears your ready state. Leave this room?"}</p>
      </BottomSheet>
    </div>
  );
}

function readyNote(
  member: { ready?: boolean },
  { isReadyCheck, isHost, t }: { isReadyCheck: boolean; isHost: boolean; t: ReturnType<typeof useLocale>["t"] },
) {
  if (!isReadyCheck) return isHost ? <span className="seat-pass">{t("host")}</span> : undefined;
  if (member.ready === true) return <span className="seat-pass seat-ready">{t("ready")}</span>;
  return undefined;
}

function rulesetName(id: string, t: ReturnType<typeof useLocale>["t"]): string {
  if (id === "casual") return t("casual");
  if (id === "house") return t("house");
  return t("standard");
}
